import { memo, useCallback, useState, useMemo } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import DeleteButton from 'src/components/Filters/DeleteButton';
import Scrollbar from 'src/components/Scrollbar';
import ActiveFilterSection from 'src/components/Filters/ActiveFilterSection';
import SidebarListItem from 'src/components/Filters/SidebarListItem';
import SidebarFooter from 'src/components/Filters/SidebarFooter';
import { defaultDeviceLogsFilters } from 'src/components/Filters/constants';
import { pagesText } from 'src/constants/pagesText';

const sidebarList = [
  {
    title: 'Date',
    name: 'DateFilter',
  },
  {
    title: 'Type',
    name: 'logTypes',
  },
];

const FilterSidebarTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontWeight: 500,
  fontSize: theme.typography.pxToRem(18),
  lineHeight: '24px',
  marginLeft: theme.spacing(3.5),
}));

const SidebarDivider = () => (
  <Divider
    ornentation="horizontal"
    sx={{
      backgroundColor: '#2C2C2C',
    }}
  />
);

const LogFilterSidebar = ({ open, onClose, onApplyFilters }) => {
  const [activeFilterSection, setActiveFilterSection] = useState(null);
  const initialFilters = useMemo(
    () => ({
      DateFilter: defaultDeviceLogsFilters.DateFilter,
      logTypes: defaultDeviceLogsFilters.logTypes,
    }),
    [],
  );

  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);

  const onChangeActiveSidebar = useCallback(
    activeSection => {
      setActiveFilterSection(activeSection);
    },
    [setActiveFilterSection],
  );

  const onDrawerClose = useCallback(() => {
    onClose();
    setActiveFilterSection(null);
    setSelectedFilters(filters);
  }, [onClose, setActiveFilterSection, setSelectedFilters, filters]);

  const getFilterDate = useCallback((newDate, label) => {
    if (!newDate) return '';

    if (label === 'To') {
      let date = new Date(newDate);
      date.setUTCHours(23, 59, 0, 0);
      date.setDate(date.getDate() + 1);

      return date.toISOString();
    }

    let date = new Date(newDate);
    date.setDate(date.getDate() + 1);
    date.setUTCHours(0, 0, 0, 0);

    return date.toISOString();
  }, []);

  const onChangeFilterValue = useCallback(
    ({ source, fieldIndex, sectionIndex, fieldValue, label }) => {
      setSelectedFilters(prevState => {
        const newSourceData = [...prevState[source]];
        newSourceData[sectionIndex] = newSourceData[sectionIndex].map((filterField, index) => {
          if (index === fieldIndex) {
            return {
              ...filterField,
              value: source === 'date' ? getFilterDate(fieldValue, label) : fieldValue,
            };
          }

          return filterField;
        });

        return {
          ...prevState,
          [source]: newSourceData,
        };
      });
    },
    [getFilterDate],
  );

  const onAddFilter = useCallback(() => {
    const selectedFilterField = activeFilterSection.name;

    setSelectedFilters(prevState => ({
      ...prevState,
      [selectedFilterField]: [
        ...prevState[selectedFilterField],
        ...defaultDeviceLogsFilters[selectedFilterField],
      ],
    }));
  }, [activeFilterSection?.name, setSelectedFilters]);

  const onRemoveFilter = useCallback(
    sectionIndex => {
      const selectedFilterField = activeFilterSection.name;
      setSelectedFilters(prevState => ({
        ...prevState,
        [selectedFilterField]: prevState[selectedFilterField].filter(
          (filter, filterIndex) => filterIndex !== sectionIndex,
        ),
      }));
    },
    [activeFilterSection?.name, setSelectedFilters],
  );

  const resetFilters = useCallback(() => {
    if (!activeFilterSection) {
      setFilters(defaultDeviceLogsFilters);
      setSelectedFilters(defaultDeviceLogsFilters);
      return;
    }

    setFilters(prevState => ({
      ...prevState,
      [activeFilterSection.name]: defaultDeviceLogsFilters[activeFilterSection.name],
    }));
    setSelectedFilters(prevState => ({
      ...prevState,
      [activeFilterSection.name]: defaultDeviceLogsFilters[activeFilterSection.name],
    }));
    setActiveFilterSection(null);
  }, [activeFilterSection]);

  const onSuccess = useCallback(() => {
    if (!!activeFilterSection) {
      setFilters(prevState => ({
        ...prevState,
        [activeFilterSection.name]: selectedFilters[activeFilterSection.name],
      }));
      setActiveFilterSection(null);
      return;
    }

    onApplyFilters(selectedFilters, 'apply');
    onDrawerClose();
  }, [activeFilterSection, onApplyFilters, selectedFilters, onDrawerClose]);

  const onBackClick = useCallback(() => {
    setActiveFilterSection(null);
  }, [setActiveFilterSection]);

  const isApplyFiltersDisabled = useMemo(() => {
    if (!activeFilterSection) return false;

    const activeFilterSectionFilters = selectedFilters[activeFilterSection.name];
    if (!activeFilterSectionFilters) return true;

    return activeFilterSectionFilters.flat().every(filter => filter.value === '');
  }, [activeFilterSection, selectedFilters]);

  const checkIsFilterListItemActive = useCallback(
    name => filters[name].flat().some(item => item.value !== ''),
    [filters],
  );

  const fetchFunctionsMap = useMemo(() => ({}), []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onDrawerClose}
      PaperProps={{
        sx: theme => ({
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(6, 0),
          width: '100%',
          maxWidth: '344px',
          backgroundImage: 'unset',
        }),
      }}>
      <Box position="relative" height="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 1.5,
          }}>
          <FilterSidebarTitle>Filter By Column</FilterSidebarTitle>
          <DeleteButton
            onClick={resetFilters}
            hasIcon={false}
            styles={{
              marginRight: '28px',
            }}>
            {pagesText.filters.resetFiltersButton}
          </DeleteButton>
        </Stack>
        <SidebarDivider />
        <Scrollbar
          height="calc(100% - 80px)"
          styles={{
            padding: '0 22px',
          }}>
          {activeFilterSection ? (
            <ActiveFilterSection
              title={activeFilterSection.title}
              source={activeFilterSection.name}
              filters={selectedFilters[activeFilterSection.name]}
              onChangeFilterValue={onChangeFilterValue}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onBackClick={onBackClick}
              fetchFunctionsMap={fetchFunctionsMap}
            />
          ) : (
            <Box display="flex" mt={2.5} flexDirection="column" gap={2.25}>
              {sidebarList.map(({ title, name }) => (
                <SidebarListItem
                  title={title}
                  isActive={checkIsFilterListItemActive(name)}
                  onClick={() => onChangeActiveSidebar({ title, name })}
                />
              ))}
            </Box>
          )}
        </Scrollbar>
        <SidebarFooter
          onClose={onDrawerClose}
          onSuccess={onSuccess}
          isApplyFiltersDisabled={isApplyFiltersDisabled}
          isActiveFilterSection={!!activeFilterSection?.name}
        />
      </Box>
    </Drawer>
  );
};

export default memo(LogFilterSidebar);
